import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Loader2, Mic, Triangle, Volume2 } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { useToast } from '@/components/Toast'
import { apiClient, unwrap } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'
import { cn } from '@/lib/cn'

interface PlatformVoice {
  voiceId: string
  name: string
  persona: 'jarvis' | 'ultron' | 'ares' | 'symbiote' | 'friday'
  description: string
  blurb: string
  introScript?: string
  variant?: 'masculine' | 'feminine'
}

interface VoiceSettings {
  voices: PlatformVoice[]
  currentVoiceId: string | null
  effectiveVoiceId: string
  agentId: string | null
  voiceEnabled: boolean
}

interface TestVoiceResponse {
  audioUrl: string
}

export function VoiceCard() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [previewing, setPreviewing] = useState<string | null>(null)

  const settings = useQuery<VoiceSettings>({
    queryKey: ['voice-settings'],
    queryFn: () => unwrap<VoiceSettings>(apiClient.get('/setup/voice')),
    enabled: HAS_AUTH,
  })

  const save = useMutation({
    mutationFn: (voiceId: string) =>
      unwrap(apiClient.post('/setup/voice', { voiceId })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voice-settings'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      toast.success('Voice updated', 'Jarvis will answer in this voice on the next call.')
    },
    onError: (e) => toast.error("Couldn't update voice", (e as Error).message),
  })

  const preview = useMutation({
    mutationFn: (voiceId: string) =>
      unwrap<TestVoiceResponse>(apiClient.post('/setup/voices/test', { voiceId })),
    onSuccess: (data, voiceId) => {
      const audio = new Audio(data.audioUrl)
      audio.onended = () => setPreviewing((p) => (p === voiceId ? null : p))
      audio.onerror = () => setPreviewing(null)
      void audio.play().catch(() => setPreviewing(null))
    },
    onError: (e) => {
      setPreviewing(null)
      toast.error('Preview failed', (e as Error).message)
    },
  })

  const data = settings.data
  const voices = data?.voices ?? []
  const effective = data?.effectiveVoiceId

  const symbioteVoices = voices.filter((v) => v.persona === 'symbiote')
  const singleVoices = voices.filter((v) => v.persona !== 'symbiote')

  const handlePreview = (voiceId: string) => {
    setPreviewing(voiceId)
    preview.mutate(voiceId)
  }
  const handleSave = (voiceId: string) => save.mutate(voiceId)

  return (
    <JarvisCard className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">Voice</h3>
            <span className="text-[10px] uppercase tracking-widest text-jarvis-blue bg-jarvis-blue/10 border border-jarvis-blue/20 rounded px-1.5 py-0.5">
              Platform
            </span>
          </div>
          <p className="text-sm text-jarvis-muted mt-1">
            Pick the voice your Jarvis uses on phone calls. All three voices run on
            our ElevenLabs account — no setup or extra billing on your end.
          </p>
        </div>
        {data?.voiceEnabled && (
          <span className="flex items-center gap-1.5 text-xs text-jarvis-green flex-shrink-0">
            <CheckCircle2 size={14} />
            Provisioned
          </span>
        )}
      </div>

      {settings.isLoading && (
        <div className="text-sm text-jarvis-muted">Loading voices…</div>
      )}

      {!settings.isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {singleVoices.map((voice) => {
            const isSelected = voice.voiceId === effective
            const isPlaying = previewing === voice.voiceId
            const isSaving = save.isPending && save.variables === voice.voiceId
            return (
              <div
                key={voice.voiceId}
                className={cn(
                  'flex flex-col gap-3 rounded-lg border p-4 transition-colors',
                  isSelected
                    ? 'border-jarvis-blue bg-jarvis-blue/10'
                    : 'border-jarvis-border bg-jarvis-surface-2/40 hover:border-jarvis-blue/40',
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold">{voice.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-jarvis-muted mt-0.5">
                      {voice.persona} persona
                    </div>
                  </div>
                  {isSelected && (
                    <span className="text-[10px] text-jarvis-blue flex items-center gap-1">
                      <CheckCircle2 size={11} /> Current
                    </span>
                  )}
                </div>
                <p className="text-xs text-jarvis-muted leading-relaxed flex-1">
                  {voice.blurb}
                </p>
                <p className="text-[11px] text-jarvis-muted/80">
                  {voice.description}
                </p>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => handlePreview(voice.voiceId)}
                    disabled={preview.isPending}
                    className="text-xs flex-1 px-3 py-1.5 bg-jarvis-surface border border-jarvis-border rounded-lg hover:bg-jarvis-blue/10 hover:border-jarvis-blue/40 hover:text-jarvis-blue transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {isPlaying ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Volume2 size={12} />
                    )}
                    Preview
                  </button>
                  {!isSelected && (
                    <button
                      type="button"
                      onClick={() => handleSave(voice.voiceId)}
                      disabled={save.isPending}
                      className="text-xs flex-1 px-3 py-1.5 bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5"
                    >
                      {isSaving && <Loader2 size={12} className="animate-spin" />}
                      Use this
                    </button>
                  )}
                </div>
              </div>
            )
          })}
          {symbioteVoices.length === 2 && (
            <SymbioteCard
              voices={symbioteVoices}
              currentVoiceId={effective ?? null}
              previewing={previewing}
              isPreviewPending={preview.isPending}
              isSavePending={save.isPending}
              savingVoiceId={(save.variables as string | undefined) ?? null}
              onPreview={handlePreview}
              onSelect={handleSave}
            />
          )}
        </div>
      )}

      {data?.agentId && (
        <div className="text-xs text-jarvis-muted pt-2 border-t border-jarvis-border/50">
          <Mic size={11} className="inline mr-1.5" />
          Voice agent provisioned ({data.agentId.slice(0, 8)}…). Test it with the
          phone number on your Command Center.
        </div>
      )}
    </JarvisCard>
  )
}

interface SymbioteCardProps {
  voices: PlatformVoice[]
  currentVoiceId: string | null
  previewing: string | null
  isPreviewPending: boolean
  isSavePending: boolean
  savingVoiceId: string | null
  onPreview: (voiceId: string) => void
  onSelect: (voiceId: string) => void
}

function SymbioteCard({
  voices,
  currentVoiceId,
  previewing,
  isPreviewPending,
  isSavePending,
  savingVoiceId,
  onPreview,
  onSelect,
}: SymbioteCardProps) {
  const masculine = voices.find((v) => v.variant === 'masculine')
  const feminine = voices.find((v) => v.variant === 'feminine')
  if (!masculine || !feminine) return null

  const isActive =
    currentVoiceId === masculine.voiceId || currentVoiceId === feminine.voiceId

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border p-4 transition-colors',
        isActive
          ? 'border-jarvis-blue bg-jarvis-blue/10'
          : 'border-jarvis-border bg-jarvis-surface-2/40 hover:border-jarvis-blue/40',
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-jarvis-surface-2 border border-jarvis-border flex items-center justify-center">
            <Triangle size={13} className="text-jarvis-text rotate-180" />
          </span>
          <div>
            <div className="text-sm font-semibold">SYMBIOTE</div>
            <div className="text-[11px] uppercase tracking-widest text-jarvis-muted mt-0.5">
              Two voices · one mind
            </div>
          </div>
        </div>
        {isActive && (
          <span className="text-[10px] text-jarvis-blue flex items-center gap-1">
            <CheckCircle2 size={11} /> Current
          </span>
        )}
      </div>

      <p className="text-xs text-jarvis-muted leading-relaxed">
        The host and the alien. Pick the register that fits.
      </p>

      <div className="flex flex-col gap-2">
        {[masculine, feminine].map((voice) => {
          const isSelected = currentVoiceId === voice.voiceId
          const isPlaying = previewing === voice.voiceId
          const isSaving = isSavePending && savingVoiceId === voice.voiceId
          return (
            <div
              key={voice.voiceId}
              className={cn(
                'rounded-md border p-2.5 transition-colors',
                isSelected
                  ? 'border-jarvis-blue bg-jarvis-blue/5'
                  : 'border-jarvis-border bg-jarvis-surface/30',
              )}
            >
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <div className="text-xs font-semibold">
                  {voice.variant === 'masculine' ? 'Masculine' : 'Feminine'}
                </div>
                {isSelected && (
                  <span className="text-[10px] text-jarvis-blue">Selected</span>
                )}
              </div>
              <p className="text-[11px] text-jarvis-muted/80 leading-snug mb-2">
                {voice.description}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onPreview(voice.voiceId)}
                  disabled={isPreviewPending}
                  className="text-xs flex-1 px-2 py-1 bg-jarvis-surface border border-jarvis-border rounded hover:bg-jarvis-blue/10 hover:border-jarvis-blue/40 hover:text-jarvis-blue transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {isPlaying ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Volume2 size={11} />
                  )}
                  Preview
                </button>
                {!isSelected && (
                  <button
                    type="button"
                    onClick={() => onSelect(voice.voiceId)}
                    disabled={isSavePending}
                    className="text-xs flex-1 px-2 py-1 bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 rounded font-medium transition-colors flex items-center justify-center gap-1.5"
                  >
                    {isSaving && <Loader2 size={11} className="animate-spin" />}
                    Use this
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
